import { req } from "./transport";
import type { AdminAnalyticsPeriod, AdminFinancePeriod, AdminFinanceAnalytics, AnalyticsEvent, AdminVisitorAnalytics, AdminAcquisitionAnalytics, AdminMemberEventAnalytics, AdminRetentionAnalytics, AdminFunnelAnalytics } from "./types";

export const analyticsApi = {
  async analyticsConfig(signal?: AbortSignal) {
    return req<{ enabled: boolean; rawEventDays: number; visitorDays: number; memberOptedOut: boolean }>("/analytics/config", signal ? { signal } : {});
  },

  async analyticsEvent(input: AnalyticsEvent) {
    return req<{ ok: true }>("/analytics/event", { method: "POST", body: JSON.stringify(input) });
  },

  async objectToAnalytics(browserKey?: string) {
    return req<{ ok: true }>("/analytics/objection", { method: "POST", body: JSON.stringify(browserKey ? { browserKey } : {}) });
  },

  // Batch item 9: analytics dashboard
  async adminAnalytics() {
    return req<{
      growthByDay: { day: string; signups: number; contracts: number }[];
      growthStart: string;
      growthEnd: string;
      bookingsByMonth: { month: string; count: number }[];
      revenueByMonth: { month: string; revenue: number; gmv: number }[];
      topCategories: { category: string; count: number }[];
      usersByMonth: { month: string; count: number; total: number }[];
      escrowByMonth: { month: string; funded: number; released: number }[];
      payoutsByMonth: { month: string; amount: number }[];
      totals: { revenue: number; gmv: number; users: number; escrowHeld: number; payouts: number };
    }>("/admin/analytics");
  },

  async adminFinanceAnalytics(period: AdminFinancePeriod) {
    return req<AdminFinanceAnalytics>(`/admin/analytics/finance?${new URLSearchParams({ period })}`);
  },

  async adminRegistrationAnalytics(period: AdminAnalyticsPeriod, bucket: "day" | "week" | "month") {
    const query = new URLSearchParams({ period, bucket });
    return req<{
      kpiId: "05";
      label: string;
      unit: string;
      source: string;
      timezone: "Asia/Manila";
      period: AdminAnalyticsPeriod;
      bucket: "day" | "week" | "month";
      start: string;
      end: string;
      firstRetainedAccountAt: string | null;
      total: number;
      series: { bucket: string; count: number }[];
    }>(`/admin/analytics/registrations?${query.toString()}`);
  },

  async adminVisitorAnalytics(period: AdminAnalyticsPeriod) {
    return req<AdminVisitorAnalytics>(`/admin/analytics/visitors?${new URLSearchParams({ period })}`);
  },

  async adminAcquisitionAnalytics(period: AdminAnalyticsPeriod) {
    return req<AdminAcquisitionAnalytics>(`/admin/analytics/acquisition?${new URLSearchParams({ period })}`);
  },

  async adminMemberEventAnalytics(period: AdminAnalyticsPeriod) {
    return req<AdminMemberEventAnalytics>(`/admin/analytics/member-events?${new URLSearchParams({ period })}`);
  },

  async adminRetentionAnalytics(period: AdminAnalyticsPeriod, bucket: "day" | "week" | "month" = "week") {
    return req<AdminRetentionAnalytics>(`/admin/analytics/retention?${new URLSearchParams({ period, bucket })}`);
  },

  async adminFunnelAnalytics(period: AdminAnalyticsPeriod) {
    return req<AdminFunnelAnalytics>(`/admin/analytics/funnel?${new URLSearchParams({ period })}`);
  },

  async adminMemberActivityAnalytics() {
    return req<{
      kpiId: "08";
      label: string;
      unit: string;
      source: string;
      timezone: "Asia/Manila";
      asOf: string;
      total: number;
      activities: { activity: string; count: number; sharePercent: number | null }[];
      unclassified: { count: number; sharePercent: number | null };
      note: string;
    }>("/admin/analytics/member-activities");
  },

  async adminProfileCompletionAnalytics() {
    return req<{
      kpiId: "09";
      label: string;
      unit: string;
      source: string;
      timezone: "Asia/Manila";
      asOf: string;
      worker: { complete: number; eligible: number; ratePercent: number | null };
      company: { complete: number; eligible: number; ratePercent: number | null };
      note: string;
    }>("/admin/analytics/profile-completion");
  },

  async adminActiveListingAnalytics() {
    return req<{
      kpiId: "15";
      label: string;
      unit: string;
      source: string;
      timezone: "Asia/Manila";
      asOf: string;
      total: number;
      byKind: { kind: string; count: number }[];
      historicalAvailable: false;
      note: string;
    }>("/admin/analytics/active-listings");
  },

  async adminSupplyDemandAnalytics() {
    type SegmentPath = {
      supplyRecords: number; demandRecords: number; missingSupply: number; missingDemand: number;
      segments: { city: string; categoryOrSkill: string; supply: number; demand: number; ratio: number | null; reason: string | null }[];
      note: string;
    };
    return req<{
      kpiId: "26"; label: string; unit: string; source: string; timezone: "Asia/Manila";
      asOf: string; historicalAvailable: false;
      jobseekersToJobs: SegmentPath; servicesToProjectsRfqs: SegmentPath;
    }>("/admin/analytics/supply-demand");
  },

  async adminResponseAnalytics(period: AdminAnalyticsPeriod) {
    return req<{
      kpiId: "16";
      label: string;
      unit: string;
      source: string;
      timezone: "Asia/Manila";
      period: AdminAnalyticsPeriod;
      start: string;
      end: string;
      total: number;
      byType: { type: string; count: number }[];
      note: string;
    }>(`/admin/analytics/responses?${new URLSearchParams({ period })}`);
  },

  async adminMatchAnalytics(period: AdminAnalyticsPeriod) {
    return req<{
      kpiId: "17";
      label: string;
      unit: string;
      source: string;
      timezone: "Asia/Manila";
      period: AdminAnalyticsPeriod;
      start: string;
      end: string;
      available: boolean;
      collectionStartedAt: string | null;
      observedStart: string | null;
      partialCoverage: boolean;
      coverageReason: string | null;
      reason: string | null;
      total: number | null;
      byPath: { path: string; count: number }[] | null;
    }>(`/admin/analytics/matches?${new URLSearchParams({ period })}`);
  },

  async adminContractConversionAnalytics(period: AdminAnalyticsPeriod) {
    return req<{
      kpiId: "19"; label: string; unit: string; source: string; timezone: "Asia/Manila";
      period: AdminAnalyticsPeriod; start: string; end: string;
      cohortRange: { start: string | null; end: string }; partialCoverage: boolean;
      cohortMatureBefore: string; collectionStartedAt: string | null; available: boolean; reason: string | null;
      byPath: { path: string; awards: number; accepted: number; ratePercent: number | null }[] | null;
    }>(`/admin/analytics/contract-conversion?${new URLSearchParams({ period })}`);
  },

  async adminTimeToMatchAnalytics(period: AdminAnalyticsPeriod) {
    return req<{
      kpiId: "27"; label: string; unit: "hours"; source: string; timezone: "Asia/Manila";
      period: AdminAnalyticsPeriod; start: string; end: string; collectionStartedAt: string | null;
      observedStart: string | null; partialCoverage: boolean; coverageReason: string | null;
      available: boolean; reason: string | null; note?: string;
      byPath: { path: string; newPosts: number; matched: number; unmatchedNew: number; meanHours: number | null; reason: string | null }[] | null;
    }>(`/admin/analytics/time-to-match?${new URLSearchParams({ period })}`);
  },

  async adminActivationAnalytics(period: AdminAnalyticsPeriod) {
    return req<{
      kpiId: "10"; label: string; unit: string; source: string; timezone: "Asia/Manila";
      period: AdminAnalyticsPeriod; start: string; end: string; cohortMatureBefore: string;
      eligible: number; activated: number; ratePercent: number | null; reason: string | null;
    }>(`/admin/analytics/activation?${new URLSearchParams({ period })}`);
  },

  async adminRepeatTransactionAnalytics(period: AdminAnalyticsPeriod) {
    return req<{
      kpiId: "24"; label: string; unit: string; source: string; timezone: "Asia/Manila";
      period: AdminAnalyticsPeriod; start: string; end: string; collectionStartedAt: string | null;
      observedStart: string | null; partialCoverage: boolean; coverageReason: string | null;
      available: boolean; reason: string | null; buyers: number | null; repeatBuyers: number | null; ratePercent: number | null;
    }>(`/admin/analytics/repeat-transactions?${new URLSearchParams({ period })}`);
  }
};
